import React, { useEffect, useState } from 'react';
import axios from 'axios';
import { Plus, Pencil, Trash2, X, Eye, EyeOff, GripVertical } from 'lucide-react';
import AdminLayout from '../components/AdminLayout';
import MobileListCard from '../components/MobileListCard';
import { useAdminAuth, API } from '../context/AdminAuthContext';
import { useCategories } from '../context/CategoriesContext';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Textarea } from '../components/ui/textarea';
import { Switch } from '../components/ui/switch';
import ImageUpload, { resolveImageUrl } from '../components/ImageUpload';
import EmailTagInput from '../components/EmailTagInput';
import { useToast } from '../hooks/use-toast';

const blank = () => ({
  slug: '',
  name: '',
  tagline: '',
  desc: '',
  image: '',
  hero_banner_image: '',
  color: '#0078D4',
  bg: '#DEECF9',
  is_visible: true,
  order: 0,
  show_sort_filter: true,
  show_tag_filter: true,
  show_size_filter: true,
  sort_filter_label: 'Sort by',
  tag_filter_label: 'Tags',
  size_filter_label: 'Sizes',
  tag_options: [],
  size_options: [],
  custom_filters: [],
  // Feb 2026 — per-division order-notification recipients.
  notify_emails: [],
  // Feb 2026 — Dispatch method. 'FEFO' (default) sorts by expiry
  // date for food. 'FIFO' is an independent planner that sorts by
  // production_date for packing materials / non-food SKUs.
  dispatch_method: 'FEFO',
});

const slugify = (s) =>
  (s || '')
    .toLowerCase()
    .trim()
    .replace(/[^a-z0-9]+/g, '-')
    .replace(/^-+|-+$/g, '');

const AdminCategories = () => {
  const { authHeaders } = useAdminAuth();
  const { refresh: refreshPublic } = useCategories();
  const { toast } = useToast();
  const [categories, setCategories] = useState([]);
  const [productCounts, setProductCounts] = useState({});
  const [loading, setLoading] = useState(true);
  const [editing, setEditing] = useState(null); // null | 'new' | category
  const [form, setForm] = useState(blank());
  const [saving, setSaving] = useState(false);

  const load = async () => {
    setLoading(true);
    try {
      const [cr, pr] = await Promise.all([
        axios.get(`${API}/admin/categories`, { headers: authHeaders }),
        axios.get(`${API}/admin/products`, { headers: authHeaders }),
      ]);
      setCategories(cr.data);
      const counts = {};
      pr.data.forEach((p) => {
        counts[p.category_slug] = (counts[p.category_slug] || 0) + 1;
      });
      setProductCounts(counts);
    } catch (e) {
      console.error(e);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    load();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const openNew = () => {
    const maxOrder = categories.reduce((m, c) => Math.max(m, c.order || 0), 0);
    setForm({ ...blank(), order: maxOrder + 1 });
    setEditing('new');
  };

  const openEdit = (c) => {
    setForm({
      slug: c.slug,
      name: c.name,
      tagline: c.tagline || '',
      desc: c.desc || '',
      image: c.image || '',
      hero_banner_image: c.hero_banner_image || '',
      color: c.color || '#0078D4',
      bg: c.bg || '#DEECF9',
      is_visible: c.is_visible,
      order: c.order ?? 0,
      show_sort_filter: c.show_sort_filter !== false,
      show_tag_filter: c.show_tag_filter !== false,
      show_size_filter: c.show_size_filter !== false,
      sort_filter_label: c.sort_filter_label || 'Sort by',
      tag_filter_label: c.tag_filter_label || 'Tags',
      size_filter_label: c.size_filter_label || 'Sizes',
      tag_options: c.tag_options || [],
      size_options: c.size_options || [],
      custom_filters: c.custom_filters || [],
      notify_emails: c.notify_emails || [],
      dispatch_method: (c.dispatch_method || 'FEFO').toUpperCase() === 'FIFO' ? 'FIFO' : 'FEFO',
    });
    setEditing(c);
  };

  const close = () => setEditing(null);

  const toggleVisibility = async (c) => {
    try {
      await axios.put(
        `${API}/admin/categories/${c.id}`,
        { is_visible: !c.is_visible },
        { headers: authHeaders },
      );
      toast({
        title: c.is_visible ? 'Category hidden' : 'Category shown',
        description: `${c.name} is now ${c.is_visible ? 'hidden from' : 'visible on'} the storefront.`,
      });
      await load();
      await refreshPublic();
    } catch (err) {
      toast({ title: 'Update failed', description: err.response?.data?.detail || 'Try again' });
    }
  };

  const save = async (e) => {
    e.preventDefault();
    if (!form.name.trim()) {
      toast({ title: 'Name required', description: 'Please enter a division name.' });
      return;
    }
    setSaving(true);
    try {
      const payload = {
        ...form,
        slug: slugify(form.slug || form.name),
        order: parseInt(form.order, 10) || 0,
      };
      if (editing === 'new') {
        await axios.post(`${API}/admin/categories`, payload, { headers: authHeaders });
        toast({ title: 'Division created', description: payload.name });
      } else {
        await axios.put(`${API}/admin/categories/${editing.id}`, payload, { headers: authHeaders });
        toast({ title: 'Division updated', description: payload.name });
      }
      close();
      await load();
      await refreshPublic();
    } catch (err) {
      toast({ title: 'Save failed', description: err.response?.data?.detail || 'Try again' });
    } finally {
      setSaving(false);
    }
  };

  const remove = async (c) => {
    const count = productCounts[c.slug] || 0;
    if (count > 0) {
      toast({
        title: 'Cannot delete',
        description: `${count} product(s) still in "${c.name}". Move or delete them first, or hide the category instead.`,
      });
      return;
    }
    if (!window.confirm(`Delete category "${c.name}"? This cannot be undone.`)) return;
    try {
      await axios.delete(`${API}/admin/categories/${c.id}`, { headers: authHeaders });
      toast({ title: 'Division deleted', description: c.name });
      await load();
      await refreshPublic();
    } catch (err) {
      toast({ title: 'Delete failed', description: err.response?.data?.detail || 'Try again' });
    }
  };

  return (
    <AdminLayout>
      <div className="p-6 lg:p-10">
        <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4 mb-8">
          <div>
            <h1 className="text-3xl font-extrabold text-[#0F172A] font-display">
              Divisions
            </h1>
            <p className="text-neutral-500 mt-1">
              Add, edit, hide or remove product divisions. Hidden divisions won't appear on the storefront.
            </p>
          </div>
          <Button
            onClick={openNew}
            className="h-11 px-5 rounded-full bg-[#0078D4] hover:bg-[#005A9E] text-white font-semibold btn-lift"
            data-testid="add-category-btn"
          >
            <Plus className="w-4 h-4 mr-1" /> Add division
          </Button>
        </div>

        <div className="bg-white rounded-2xl border border-neutral-200 overflow-hidden">
          {loading ? (
            <div className="p-10 text-center text-neutral-500">Loading…</div>
          ) : categories.length === 0 ? (
            <div className="p-10 text-center text-neutral-500">No divisions yet. Add your first one.</div>
          ) : (
            <>
              {/* Mobile card list — Feb 2026 native-app pass */}
              <div className="md:hidden p-3 space-y-2" data-testid="admin-categories-list-mobile">
                {categories.map((c) => {
                  const count = productCounts[c.slug] || 0;
                  return (
                    <MobileListCard
                      key={c.id}
                      testId={`category-card-${c.slug}`}
                      title={
                        <div className="flex items-center gap-2">
                          <span
                            className="w-8 h-8 rounded-lg flex items-center justify-center flex-shrink-0 overflow-hidden"
                            style={{ background: c.bg }}
                          >
                            {c.image ? (
                              <img src={resolveImageUrl(c.image)} alt={c.name} className="max-w-full max-h-full object-contain" />
                            ) : (
                              <span className="w-3 h-3 rounded-full" style={{ background: c.color }} />
                            )}
                          </span>
                          <span className="truncate">{c.name}</span>
                        </div>
                      }
                      statusPill={
                        <span className={`px-2 py-0.5 rounded-full text-[10px] font-bold ${c.is_visible ? 'bg-emerald-50 border border-emerald-200 text-emerald-700' : 'bg-neutral-100 border border-neutral-200 text-neutral-500'}`}>
                          {c.is_visible ? 'Shown' : 'Hidden'}
                        </span>
                      }
                      businessName={c.tagline || c.slug}
                      customerName={`${count} product${count === 1 ? '' : 's'}`}
                      amount={`Order · ${c.order ?? 0}`}
                      date={c.slug}
                      onClick={() => openEdit(c)}
                      actions={[
                        { label: 'Edit', onClick: () => openEdit(c), testId: `category-card-edit-${c.slug}` },
                        { label: c.is_visible ? 'Hide' : 'Show', onClick: () => toggleVisibility(c) },
                        { divider: true },
                        { label: 'Delete', onClick: () => remove(c), danger: true, testId: `category-card-delete-${c.slug}` },
                      ]}
                    />
                  );
                })}
              </div>

              {/* Desktop table — hidden on <md */}
              <div className="hidden md:block overflow-x-auto">
              <table className="w-full text-sm">
                <thead className="bg-neutral-50 text-left text-xs uppercase tracking-wide text-neutral-500">
                  <tr>
                    <th className="px-6 py-3 w-12">
                      <GripVertical className="w-4 h-4 text-neutral-300" />
                    </th>
                    <th className="px-6 py-3">Division</th>
                    <th className="px-6 py-3">Slug</th>
                    <th className="px-6 py-3">Tagline</th>
                    <th className="px-6 py-3">Products</th>
                    <th className="px-6 py-3">Order</th>
                    <th className="px-6 py-3">Visible</th>
                    <th className="px-6 py-3 text-right">Actions</th>
                  </tr>
                </thead>
                <tbody>
                  {categories.map((c) => {
                    const count = productCounts[c.slug] || 0;
                    return (
                      <tr
                        key={c.id}
                        className="border-t border-neutral-100 hover:bg-neutral-50"
                        data-testid={`category-row-${c.slug}`}
                      >
                        <td className="px-6 py-3.5 text-neutral-300">
                          <GripVertical className="w-4 h-4" />
                        </td>
                        <td className="px-6 py-3.5">
                          <div className="flex items-center gap-3">
                            <div
                              className="w-10 h-10 rounded-lg flex items-center justify-center flex-shrink-0"
                              style={{ background: c.bg }}
                            >
                              {c.image ? (
                                <img src={resolveImageUrl(c.image)} alt={c.name} className="max-w-full max-h-full object-contain" />
                              ) : (
                                <div className="w-4 h-4 rounded-full" style={{ background: c.color }} />
                              )}
                            </div>
                            <div className="font-medium text-[#0F172A]">{c.name}</div>
                          </div>
                        </td>
                        <td className="px-6 py-3.5 text-neutral-500 font-mono text-xs">{c.slug}</td>
                        <td className="px-6 py-3.5 text-neutral-600">{c.tagline || '—'}</td>
                        <td className="px-6 py-3.5 text-neutral-600">{count}</td>
                        <td className="px-6 py-3.5 text-neutral-600">{c.order ?? 0}</td>
                        <td className="px-6 py-3.5">
                          <button
                            onClick={() => toggleVisibility(c)}
                            className={`inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full text-[11px] font-semibold transition-colors ${
                              c.is_visible
                                ? 'bg-emerald-100 text-emerald-700 hover:bg-emerald-200'
                                : 'bg-neutral-200 text-neutral-600 hover:bg-neutral-300'
                            }`}
                            data-testid={`toggle-visibility-${c.slug}`}
                          >
                            {c.is_visible ? (
                              <>
                                <Eye className="w-3 h-3" /> Shown
                              </>
                            ) : (
                              <>
                                <EyeOff className="w-3 h-3" /> Hidden
                              </>
                            )}
                          </button>
                        </td>
                        <td className="px-6 py-3.5 text-right">
                          <button
                            onClick={() => openEdit(c)}
                            className="p-2 hover:bg-neutral-100 rounded-md text-neutral-700"
                            data-testid={`edit-category-${c.slug}`}
                          >
                            <Pencil className="w-4 h-4" />
                          </button>
                          <button
                            onClick={() => remove(c)}
                            className="p-2 hover:bg-red-50 rounded-md text-red-500 disabled:opacity-40 disabled:hover:bg-transparent"
                            disabled={count > 0}
                            title={count > 0 ? `Cannot delete: ${count} product(s) in this category` : 'Delete'}
                            data-testid={`delete-category-${c.slug}`}
                          >
                            <Trash2 className="w-4 h-4" />
                          </button>
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
              </div>
            </>
          )}
        </div>
      </div>

      {/* Edit drawer */}
      {editing && (
        <div className="fixed inset-0 z-[60]">
          <div className="absolute inset-0 bg-black/40" onClick={close} />
          <div className="absolute right-0 top-0 bottom-0 w-full max-w-md bg-white shadow-2xl flex flex-col">
            <div className="p-5 border-b border-neutral-200 flex items-center justify-between">
              <h3 className="text-lg font-bold">{editing === 'new' ? 'Add category' : 'Edit category'}</h3>
              <button onClick={close} className="p-2 hover:bg-neutral-100 rounded-full">
                <X className="w-5 h-5" />
              </button>
            </div>
            <form onSubmit={save} className="flex-1 overflow-y-auto p-5 space-y-4">
              <div>
                <label className="text-xs font-semibold text-neutral-700 uppercase tracking-wide">Name *</label>
                <Input
                  required
                  value={form.name}
                  onChange={(e) =>
                    setForm({
                      ...form,
                      name: e.target.value,
                      // Auto-fill slug on creation if user hasn't customised it
                      slug:
                        editing === 'new' && (!form.slug || form.slug === slugify(form.name))
                          ? slugify(e.target.value)
                          : form.slug,
                    })
                  }
                  className="mt-1"
                  placeholder="e.g. Glace"
                  data-testid="category-name-input"
                />
              </div>
              <div>
                <label className="text-xs font-semibold text-neutral-700 uppercase tracking-wide">Slug</label>
                <Input
                  value={form.slug}
                  onChange={(e) => setForm({ ...form, slug: e.target.value })}
                  onBlur={(e) => setForm({ ...form, slug: slugify(e.target.value) })}
                  placeholder="e.g. ice-cream"
                  className="mt-1 font-mono text-sm"
                  data-testid="category-slug-input"
                />
                <p className="text-[11px] text-neutral-500 mt-1">
                  URL-safe identifier. Used in /category/&lt;slug&gt;. Changing this updates all linked products.
                </p>
              </div>
              <div>
                <label className="text-xs font-semibold text-neutral-700 uppercase tracking-wide">Tagline</label>
                <Input
                  value={form.tagline}
                  onChange={(e) => setForm({ ...form, tagline: e.target.value })}
                  placeholder="Short pitch shown under name"
                  className="mt-1"
                />
              </div>
              <div>
                <label className="text-xs font-semibold text-neutral-700 uppercase tracking-wide">Description</label>
                <Textarea
                  rows={3}
                  value={form.desc}
                  onChange={(e) => setForm({ ...form, desc: e.target.value })}
                  placeholder="Shown on the division page hero"
                  className="mt-1"
                />
              </div>
              <div>
                <label className="text-xs font-semibold text-neutral-700 uppercase tracking-wide">Image</label>
                <div className="mt-1">
                  <ImageUpload
                    value={form.image}
                    onChange={(url) => setForm({ ...form, image: url })}
                    bg={form.bg}
                    testId="category-image"
                  />
                </div>
              </div>
              <div>
                <label className="text-xs font-semibold text-neutral-700 uppercase tracking-wide">Hero banner image (optional)</label>
                <p className="text-[11px] text-neutral-500 mb-1">Wide image shown at the top of the division page. If empty, the regular image is used.</p>
                <div className="mt-1">
                  <ImageUpload
                    value={form.hero_banner_image}
                    onChange={(url) => setForm({ ...form, hero_banner_image: url })}
                    bg={form.bg}
                    testId="category-hero-banner"
                  />
                </div>
              </div>

              {/* Filters editor */}
              <div className="border-t border-neutral-200 pt-4 mt-2">
                <div className="text-sm font-bold text-[#0F172A] mb-1">Product page filters</div>
                <p className="text-[11px] text-neutral-500 mb-3">Choose which filters appear on this category, rename them, and add custom ones (e.g. "Brand").</p>
                <div className="grid grid-cols-3 gap-2 mb-3">
                  {[
                    { key: 'show_sort_filter', label: 'Sort' },
                    { key: 'show_tag_filter', label: 'Tags' },
                    { key: 'show_size_filter', label: 'Sizes' },
                  ].map(({ key, label }) => (
                    <label key={key} className="flex items-center justify-between p-2 rounded-lg border border-neutral-200 cursor-pointer text-xs">
                      <span>{label}</span>
                      <Switch
                        checked={form[key] !== false}
                        onCheckedChange={(v) => setForm({ ...form, [key]: v })}
                      />
                    </label>
                  ))}
                </div>
                <div className="grid grid-cols-3 gap-2 mb-3">
                  <Input
                    value={form.sort_filter_label || ''}
                    onChange={(e) => setForm({ ...form, sort_filter_label: e.target.value })}
                    placeholder="Sort label"
                    className="text-xs"
                  />
                  <Input
                    value={form.tag_filter_label || ''}
                    onChange={(e) => setForm({ ...form, tag_filter_label: e.target.value })}
                    placeholder="Tags label"
                    className="text-xs"
                  />
                  <Input
                    value={form.size_filter_label || ''}
                    onChange={(e) => setForm({ ...form, size_filter_label: e.target.value })}
                    placeholder="Sizes label"
                    className="text-xs"
                  />
                </div>
                <div className="grid sm:grid-cols-2 gap-2 mb-3">
                  <div>
                    <div className="text-[10px] font-bold text-neutral-500 uppercase mb-1">Tag options (one per line)</div>
                    <Textarea
                      rows={3}
                      value={(form.tag_options || []).join('\n')}
                      onChange={(e) => setForm({ ...form, tag_options: e.target.value.split('\n').map((s) => s.trim()).filter(Boolean) })}
                      placeholder="Auto-detected from products if empty"
                      className="font-mono text-xs"
                    />
                  </div>
                  <div>
                    <div className="text-[10px] font-bold text-neutral-500 uppercase mb-1">Size options (one per line)</div>
                    <Textarea
                      rows={3}
                      value={(form.size_options || []).join('\n')}
                      onChange={(e) => setForm({ ...form, size_options: e.target.value.split('\n').map((s) => s.trim()).filter(Boolean) })}
                      placeholder="Auto-detected from products if empty"
                      className="font-mono text-xs"
                    />
                  </div>
                </div>

                {/* Custom filters */}
                <div className="text-[10px] font-bold text-neutral-500 uppercase mb-1">Custom filters</div>
                <div className="space-y-2">
                  {(form.custom_filters || []).map((f, idx) => (
                    <div key={f.id || idx} className="border border-neutral-200 rounded-lg p-2 space-y-2">
                      <div className="grid grid-cols-2 gap-2">
                        <Input
                          value={f.label}
                          placeholder="Display label (e.g. Brand)"
                          onChange={(e) =>
                            setForm((prev) => ({
                              ...prev,
                              custom_filters: prev.custom_filters.map((x, i) => (i === idx ? { ...x, label: e.target.value } : x)),
                            }))
                          }
                          className="text-xs"
                        />
                        <div className="flex items-center gap-1">
                          <Input
                            value={f.key}
                            placeholder="key (e.g. brand)"
                            onChange={(e) =>
                              setForm((prev) => ({
                                ...prev,
                                custom_filters: prev.custom_filters.map((x, i) => (i === idx ? { ...x, key: e.target.value.toLowerCase().replace(/[^a-z0-9_]/g, '') } : x)),
                              }))
                            }
                            className="text-xs font-mono"
                          />
                          <button
                            type="button"
                            onClick={() =>
                              setForm((prev) => ({
                                ...prev,
                                custom_filters: prev.custom_filters.filter((_, i) => i !== idx),
                              }))
                            }
                            className="text-red-600 hover:bg-red-50 rounded p-1"
                          >
                            <Trash2 className="w-3.5 h-3.5" />
                          </button>
                        </div>
                      </div>
                      <Textarea
                        rows={2}
                        value={(f.options || []).map((o) => o.label).join('\n')}
                        onChange={(e) =>
                          setForm((prev) => ({
                            ...prev,
                            custom_filters: prev.custom_filters.map((x, i) =>
                              i === idx
                                ? {
                                    ...x,
                                    options: e.target.value
                                      .split('\n')
                                      .map((s) => s.trim())
                                      .filter(Boolean)
                                      .map((label) => ({ label, value: label })),
                                  }
                                : x,
                            ),
                          }))
                        }
                        placeholder="Options (one per line)"
                        className="font-mono text-xs"
                      />
                    </div>
                  ))}
                  <Button
                    type="button"
                    variant="outline"
                    size="sm"
                    onClick={() =>
                      setForm({
                        ...form,
                        custom_filters: [...(form.custom_filters || []), { id: `cf-${Date.now()}`, key: '', label: '', options: [] }],
                      })
                    }
                    className="text-xs rounded-full border-dashed w-full"
                  >
                    <Plus className="w-3.5 h-3.5 mr-1" /> Add custom filter
                  </Button>
                </div>
              </div>
              <div className="grid grid-cols-2 gap-3">
                <div>
                  <label className="text-xs font-semibold text-neutral-700 uppercase tracking-wide">Accent color</label>
                  <div className="mt-1 flex items-center gap-2">
                    <input
                      type="color"
                      value={form.color}
                      onChange={(e) => setForm({ ...form, color: e.target.value })}
                      className="h-10 w-12 rounded-md border border-neutral-300 cursor-pointer"
                    />
                    <Input
                      value={form.color}
                      onChange={(e) => setForm({ ...form, color: e.target.value })}
                      className="font-mono text-sm"
                    />
                  </div>
                </div>
                <div>
                  <label className="text-xs font-semibold text-neutral-700 uppercase tracking-wide">Background</label>
                  <div className="mt-1 flex items-center gap-2">
                    <input
                      type="color"
                      value={form.bg}
                      onChange={(e) => setForm({ ...form, bg: e.target.value })}
                      className="h-10 w-12 rounded-md border border-neutral-300 cursor-pointer"
                    />
                    <Input
                      value={form.bg}
                      onChange={(e) => setForm({ ...form, bg: e.target.value })}
                      className="font-mono text-sm"
                    />
                  </div>
                </div>
              </div>
              <div>
                <label className="text-xs font-semibold text-neutral-700 uppercase tracking-wide">Display order</label>
                <Input
                  type="number"
                  step="1"
                  value={form.order}
                  onChange={(e) => setForm({ ...form, order: e.target.value })}
                  className="mt-1"
                />
                <p className="text-[11px] text-neutral-500 mt-1">Lower numbers appear first on the storefront.</p>
              </div>
              <div className="flex items-center justify-between p-3 bg-neutral-50 rounded-lg">
                <div>
                  <div className="text-sm font-semibold">Visible on storefront</div>
                  <div className="text-xs text-neutral-500">Toggle off to hide this division from customers</div>
                </div>
                <Switch
                  checked={form.is_visible}
                  onCheckedChange={(v) => setForm({ ...form, is_visible: v })}
                  className="data-[state=checked]:bg-[#0078D4]"
                  data-testid="category-visible-switch"
                />
              </div>
              {/* Feb 2026 — Dispatch method (FEFO | FIFO).
                  FEFO (default) sorts by expiry_date for food SKUs
                  and is unchanged. FIFO engages a SEPARATE, additive
                  planner that sorts by production_date — used for
                  packing materials / non-food SKUs with no expiry.
                  Both planners share the same pick-line shape so
                  downstream render / audit / release code stays
                  agnostic. See `plan_dispatch_for_lines` in
                  `routers/warehouses.py`. */}
              <div className="p-3 bg-sky-50 border border-sky-200 rounded-lg">
                <div className="mb-2">
                  <div className="text-sm font-semibold text-sky-900">Dispatch method</div>
                  <div className="text-[11px] text-neutral-600">
                    FEFO = oldest-expiry first (food, dairy, perishables).
                    FIFO = oldest-received first (packing materials, non-perishables, items without an expiry date).
                  </div>
                </div>
                <div className="grid grid-cols-2 gap-2">
                  <button
                    type="button"
                    onClick={() => setForm({ ...form, dispatch_method: 'FEFO' })}
                    className={`rounded-lg px-3 py-2 text-sm font-semibold ring-1 transition ${
                      form.dispatch_method === 'FEFO'
                        ? 'bg-sky-600 text-white ring-sky-600 shadow-sm'
                        : 'bg-white text-slate-700 ring-slate-300 hover:bg-slate-50'
                    }`}
                    data-testid="category-dispatch-fefo"
                  >
                    FEFO · by expiry
                  </button>
                  <button
                    type="button"
                    onClick={() => setForm({ ...form, dispatch_method: 'FIFO' })}
                    className={`rounded-lg px-3 py-2 text-sm font-semibold ring-1 transition ${
                      form.dispatch_method === 'FIFO'
                        ? 'bg-amber-600 text-white ring-amber-600 shadow-sm'
                        : 'bg-white text-slate-700 ring-slate-300 hover:bg-slate-50'
                    }`}
                    data-testid="category-dispatch-fifo"
                  >
                    FIFO · by received date
                  </button>
                </div>
              </div>
              {/* Feb 2026 — per-division order-notification recipients.
                  Independent of the global admin footer email and the
                  campaign auto-trigger list. When set, everyone here
                  gets a scoped email (only THIS division's items) on
                  every order placement + status change. */}
              <div className="p-3 bg-amber-50/60 border border-amber-200 rounded-lg">
                <div className="flex items-center justify-between mb-2">
                  <div>
                    <div className="text-sm font-semibold text-amber-900">Order notification recipients</div>
                    <div className="text-[11px] text-neutral-600">Get an email whenever an order contains items from this division — scoped to only your items, never the full order.</div>
                  </div>
                </div>
                <EmailTagInput
                  value={form.notify_emails || []}
                  onChange={(next) => setForm({ ...form, notify_emails: next })}
                  placeholder="e.g. glace-team@mrpco.net"
                  testId="category-notify-emails"
                />
              </div>
            </form>
            <div className="p-5 border-t border-neutral-200 flex gap-2">
              <Button type="button" variant="outline" onClick={close} className="flex-1 h-11 rounded-full">
                Cancel
              </Button>
              <Button
                onClick={save}
                disabled={saving}
                className="flex-1 h-11 rounded-full bg-[#0078D4] hover:bg-[#005A9E] text-white font-semibold"
                data-testid="save-category-btn"
              >
                {saving ? 'Saving…' : editing === 'new' ? 'Create' : 'Save changes'}
              </Button>
            </div>
          </div>
        </div>
      )}
    </AdminLayout>
  );
};

export default AdminCategories;
